import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Reads a one-time token from an emailed link, then removes it from the
 * address bar (VAPT 2026-10-16, finding 1).
 *
 * Links carry the token in the fragment (`#token=...`), which browsers never
 * send to the server or put in a Referer header. `?token=...` is still read so
 * links emailed before the change keep working until they expire.
 *
 * The token is kept in component state only. Reloading the page after it is
 * stripped shows the page's "missing link" state; opening the email link
 * again works because the token is still unused.
 */
export function readUrlToken(search: string, hash: string): string {
  const fromHash = new URLSearchParams(hash.replace(/^#/, '')).get('token');
  const fromQuery = new URLSearchParams(search).get('token');
  return fromHash || fromQuery || '';
}

export function useUrlToken(): string {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState(() => readUrlToken(location.search, location.hash));

  // Runs on mount and whenever the URL changes. Opening a second emailed
  // link in the same tab is only a hash change, so the page does not reload;
  // the new token must still be read and stripped (bug #20947).
  useEffect(() => {
    const fresh = readUrlToken(location.search, location.hash);
    if (!fresh) return;
    setToken(fresh);
    navigate({ pathname: location.pathname }, { replace: true, state: location.state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.hash]);

  return token;
}
